import { all } from '@awesome.me/kit-2cbcf7d4a0/icons';
import NavigationService from '@components-clean/page-nav/navigation.service';
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IRiskActionFields } from '@interfaces/risk-action.interface';
import { filterBy, orderBy, process, CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { getter } from '@progress/kendo-react-common';
import { HeaderThElement } from '@progress/kendo-react-data-tools';
import {
    getSelectedState,
    Grid,
    GridColumnProps,
    GridColumnReorderEvent,
    GridEvent,
    GridSelectionChangeEvent,
    GridDetailRowProps,
    GridCustomCellProps,
    GridHandle,
} from '@progress/kendo-react-grid';
import { Checkbox } from '@progress/kendo-react-inputs';
import { Tooltip } from '@progress/kendo-react-tooltip';
import cn from 'clsx';
import { cloneDeep, debounce } from 'lodash';
import React, { useEffect, useState, useRef, useLayoutEffect, useCallback } from 'react';
import './styles-v2.scss';
import {
    getGridMinWidth,
    gridV2RowRender,
    renderGridV2Columns,
} from '@components/core/expandable/grid-v2/grid-v2.helper';
import GridViewMenu from '@components/core/expandable/grid-v2/grid-view-menu/grid-view-menu';
import ExportExcelComponent from '@components/core/export-excel';
import SwitchMenu from '@components/core/grid-header/switch-menu';
import { calculateHierarchyRowWidth, CUSTOM_COLUMN_WIDTH } from '@components/core/grid-helper';
import { ICMActionFieldsExtended } from '@pages/central-monitoring/manage-actions.interface';
import GridHeader from '../../grid-header';
import GridMenu from '../../grid-header/grid-menu';
import GridPager from '../../grid-header/grid-pager';
import SettingsMenu from '../../grid-header/settings-menu';
import { cellRenderV2, configureColumnsArrayV2, showAppliedFiltersV2 } from './grid-v2.service';
import IExpandableGridPropsV2, { IExpandableGridColumnV2 } from './interfaces-v2/grid-props-v2.interface';
import { useBoolean } from 'hooks';
import Constants from 'utils/constants';
import { compareColumnsByOrderIndex, prepareForGrouping } from 'utils/grid';
import { updateGridDetailsWidth } from 'utils/grid-v2';
import ActionsMenuV2 from 'views/components/core/actions-menu-v2';
import { addDetailRowIdToGrid } from '@utils/automation-test-helpers';

// And add to the library
library.add({ ...all });

const DELETED_ITEM_FIELDS_REGEXP = /deleted|_deleteReason|_deleteDate|_deletedBy/;

const SELECTED_FIELD = 'selected';
const DATA_ITEM_KEY = 'id';
const idGetter = getter(DATA_ITEM_KEY);

const ExpandableGridV2 = ({
    id,
    title,
    items,
    columns,
    detail,
    hasDetail,
    groupable,
    scrollable,
    showPager,
    hideExpand = false,
    hideCheckbox = false,
    maxHeight,
    rowActions,
    singleRowAction,
    rowCheckbox,
    reorderable,
    resizable,
    pageable,
    customKey,
    initialFilter,
    initialSort,
    setPersistentFilter,
    onFilteredItemsChange,
    showCheckbox,
    detailClass,
    isChildGrid,
    showFirstColumn = true,
    isModalOpened = false,
    onUpdateAmount,
    hideDeletedToggle,
    showDeletedItems,
    handleSelection,
    className = '',
    setCurrentItem,
    selectable,
    extraMenuItems,
    gridViewOptions,
    defaultSelected,
    showCustomTitle,
    switchMenu,
    subHeader,
    disableTools,
    exportConfig,
    fixedScroll,
}: IExpandableGridPropsV2) => {
    const widthFirstExtraColumn = showFirstColumn ? CUSTOM_COLUMN_WIDTH : 0;

    let grid: any = null;
    const generalRef = useRef<GridHandle | null>(null);
    const [pageSize, setPageSize] = useState(25);
    const [numPages, setNumPages] = useState(1);
    const [columnsArray, setColumnsArray] = useState([]);
    const [settingsArray, setSettingsArray] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pagingState, setPagingState] = useState({ skip: 0, take: pageSize });
    const [expandedItems, setExpandedItems] = useState({} as { [index: string]: any });
    const [areAllItemsExpanded, setAreAllItemsExpanded] = useState(false);
    const [checkedRows, setCheckedRows] = useState([] as Array<any>);
    const [minGridWidth, setMinGridWidth] = useState(0);
    const [filteredItems, setFilteredItems] = useState([]);
    const [selectedState, setSelectedState] = React.useState<{
        [id: string]: boolean | number[];
    }>({});
    const [gridKeyAffix, setGridKeyAffix] = useState<number>(1);

    const isOpenSideBar = useBoolean(NavigationService.getSlideBarStatus());

    const [toShow, setToShow] = useState([]);
    const [lockedColumns, setLockedColumns] = useState([]);
    // used if showPager is true
    const [data, setData] = useState([]);
    // used if showPager is false
    const [areAllRowsSelected, setAreAllRowsSelected] = useState(false);
    const [allData, setAllData] = useState([]);
    const [filter, setFilter] = useState(null as CompositeFilterDescriptor);
    const [sort, setSort] = useState(initialSort || []);
    const [group, setGroup] = useState([]);

    const setMinWidth = useBoolean(false);
    const isLoadedInitFilter = useBoolean(false);

    const gridElement: HTMLDivElement | null = isChildGrid
        ? document.querySelector('.child-grid-wrap .expandable-grid-v2')
        : document.querySelector(`.${customKey}`);

    const gridData = !showPager ? allData : data;

    useEffect(() => {
        updateGridDetailsWidth();
    }, []);

    useEffect(() => {
        if (defaultSelected && Object.keys(selectedState).length < 2) {
            setSelectedState({ [defaultSelected.id]: true });
        }
    }, [items, defaultSelected]);

    useEffect(() => {
        if (!selectable?.enabled) {
            setSelectedState({});
        } else if (defaultSelected) {
            setSelectedState({ [defaultSelected.id]: true });
        }
    }, [selectable?.enabled]);

    useEffect(() => {
        const subscribe = NavigationService.onUpdateSideBar().subscribe((isOpen: boolean) => {
            isOpenSideBar.setValue(isOpen);
        });
        return () => {
            subscribe.unsubscribe();
        };
    }, []);

    useEffect(() => {
        // this functionality is turned off for now.
        // It should take saved settings for hidden/locked column from localStorage or remove server in future
        setLockedColumns(JSON.parse(window.localStorage.getItem(`${customKey}-lock`)) || []);
        setToShow(JSON.parse(window.localStorage.getItem(`${customKey}-to-show`)) || []);
    }, []);

    useEffect(() => {
        setFilteredItems((items && filterBy(items.slice(0), filter)) || []);
    }, [filter, items]);

    useEffect(() => {
        if (checkedRows.length) setCheckedRows(data.filter((item: any) => item.checked && !item.deleted));
    }, [data]);

    useLayoutEffect(() => {
        // eslint-disable-next-line no-use-before-define
        handleResize();
    }, [group, columnsArray]);

    const toggleDeletedColumns = (cols: IExpandableGridColumnV2[]) =>
        cols.map((item) => {
            if (item.field.match(DELETED_ITEM_FIELDS_REGEXP)) {
                if (item.field === 'deleted') {
                    item.locked = true;
                }
                item.show = showDeletedItems.value;
            }
            return item;
        });

    useEffect(() => {
        // small optimization tip. to not change column array if modal is opened
        if (!isModalOpened) {
            let newColumns = configureColumnsArrayV2(columns, toShow, settingsArray, lockedColumns);
            // Update child grid columns visibility
            if (showDeletedItems) {
                newColumns = toggleDeletedColumns(newColumns);
            }
            setColumnsArray(newColumns);
        }
    }, [columns]);

    useEffect(() => {
        setAllData(items);
    }, [items]);

    useEffect(() => {
        // Set current page to 1 when deleted items visibility is toggled
        if (currentPage !== 1) {
            setCurrentPage(1);
        }
    }, [showDeletedItems?.value]);

    // When sort, paging, filter, or items change, re-process the data
    useEffect(() => {
        let filteredByDeletedProp = filteredItems;

        if (showDeletedItems && !showDeletedItems.value) {
            filteredByDeletedProp = filteredItems.filter((item) => !item.deleted);
        }

        const result = prepareForGrouping(filteredByDeletedProp, columnsArray, group);

        if (onUpdateAmount) onUpdateAmount(result.preparedArray.length);

        if (filter && onFilteredItemsChange) onFilteredItemsChange(filteredByDeletedProp);
        setAllData(
            process(orderBy(result.preparedArray, sort), {
                group: result.grouping,
            }).data,
        );

        setData(
            process(orderBy(result.preparedArray, sort), {
                ...pagingState,
                group: result.grouping,
            }).data,
        );
    }, [pagingState, sort, filteredItems, group, showDeletedItems?.value]);

    // Update the grid data when the current page changes
    useEffect(() => {
        setPagingState({ skip: (currentPage - 1) * pageSize, take: pageSize });
    }, [currentPage]);

    // Update the amount of items per page
    useEffect(() => {
        setPagingState({ skip: 0, take: pageSize });
    }, [pageSize]);

    useEffect(() => {
        // isLoadedInitFilter trigger is used to apply initial filter only on initial rendering
        if ((data?.length || allData?.length) && initialFilter && !isLoadedInitFilter.value) {
            isLoadedInitFilter.setTrue();
            setFilter(initialFilter);
        }
    }, [initialFilter, data, allData]);

    useEffect(() => {
        // set width of custom scroll
        const array = [...columnsArray].filter((item: any) => item.show);

        const value = getGridMinWidth(array, widthFirstExtraColumn);

        setMinGridWidth(value);
        const gridElementWidth = gridElement?.offsetWidth;
        setMinWidth.setValue(gridElementWidth < value);
    }, [gridElement]);

    const expandChange = (event: any) => {
        event.dataItem.expanded =
            expandedItems[event.dataItem.id] !== undefined ? !expandedItems[event.dataItem.id] : !event.expanded;
        // eslint-disable-next-line no-use-before-define
        setExpanded(event.dataItem.id, event.dataItem.expanded);
    };

    // eslint-disable-next-line no-shadow
    const setExpanded = (id: string, expanded: boolean) => {
        const newExpandedItems: { [index: string]: any } = { ...expandedItems };
        newExpandedItems[id] = expanded;
        setExpandedItems(newExpandedItems);
    };

    const appliedFilters = showAppliedFiltersV2(filter, columns);

    const clearFilters = () => {
        setFilter({ ...filter, filters: [] });
        if (setPersistentFilter) setPersistentFilter(null);
    };

    const dataStateChange = (event: any) => {
        const currentDataSetGroup = event.dataState.group.map((groupItem: IExpandableGridColumnV2) => ({
            ...groupItem,
            field: group.find((el: IExpandableGridColumnV2) => el.id === groupItem.id)?.field ?? groupItem.field,
        }));

        setGroup(currentDataSetGroup);
        if (currentPage !== 1) setCurrentPage(1);
    };

    const handleChangeCheckedRows = (dataItem: any) => {
        const array = [...checkedRows];
        if (dataItem.checked) {
            array.push(dataItem);
            setCheckedRows(array);
        } else if (!dataItem.checked) {
            const index: number = array.findIndex((item: any) => dataItem.id === item.id);
            array.splice(index, 1);
            setCheckedRows(array);
        }
    };

    const onColumnsSubmit = (columnState: IExpandableGridColumnV2[]) => {
        setColumnsArray(columnState);
        setSettingsArray(columnState);
        setGridKeyAffix((value) => {
            return ++value;
        });
    };

    const gridKey = `${customKey}_${gridKeyAffix}`;

    const getColumn = (inputColumns: any[], field: string) =>
        inputColumns.find((column: any) => column.field === field);

    const getColumnsFromReorderedGridColumns = (reorderedGridColumns: GridColumnProps[]) =>
        reorderedGridColumns.reduce((result: any[], column: GridColumnProps) => {
            const newColumn = getColumn([...columnsArray], column.field);

            if (newColumn)
                result.push({
                    ...newColumn,
                    orderIndex: column.orderIndex,
                    children: column.children,
                });

            return result;
        }, []);

    const sortColumnsByIndex = (unsortedColumns: GridColumnProps[]) => {
        unsortedColumns.forEach((col) => {
            if (col.children as GridColumnProps[]) {
                sortColumnsByIndex(col.children);
            }
        });
    };

    const onColumnReorder = (e: GridColumnReorderEvent) => {
        const newColumns = getColumnsFromReorderedGridColumns(e.columns);

        // Hidden columns are not returned in the Reorder event, lets add them back in
        const hiddenColumns = columnsArray.filter((column: any) => !column.show);
        if (hiddenColumns.length > 0) {
            let maxIndex = Math.max(...newColumns.map((c) => c.orderIndex));
            hiddenColumns.forEach((column: any) => {
                newColumns.push({
                    ...column,
                    orderIndex: column.field === 'deleted' ? 0 : maxIndex++,
                });
            });
        }

        sortColumnsByIndex(newColumns);

        setColumnsArray(newColumns);
        setGridKeyAffix((value) => {
            return ++value;
        });
    };

    const handleResize = () => {
        // recalculate if we need to fit columns on all grid
        const gridElementWidth = gridElement?.offsetWidth;

        if (gridElementWidth < minGridWidth && !setMinWidth.value) {
            setMinWidth.setTrue();
        } else if (gridElementWidth > minGridWidth) {
            setMinWidth.setFalse();
        }
    };

    const recordHasDetails = (item: any) => (hasDetail ? hasDetail(item) : detail ? detail(item) : null);

    const onGridScroll = (event: GridEvent) => {
        // prevent parent scrolling during child scrolling
        event.nativeEvent.stopPropagation();
    };

    const gridClasses = cn('grid-component', detailClass, {
        'no-items': !filteredItems?.length,
        'hide-first-column': !showFirstColumn,
        sidebar: isOpenSideBar.value,
        'show-grouping-header': !!group.length,
    });

    const getItemDetails = ({ dataItem }: GridDetailRowProps) => {
        const isActionToShow =
            showDeletedItems?.value ||
            dataItem.actions?.find((item: ICMActionFieldsExtended | IRiskActionFields) => !item.deleted);

        const detailDataItem = detail ? detail(dataItem) : null;

        if (detail && detailDataItem != null && expandedItems[dataItem.id] && (!dataItem.actions || isActionToShow)) {
            return (
                <div className="detail-wrapper-grid">
                    <div className="vertical-line" />
                    <span className="divider-grid" />
                    <div className="detail-element" key={dataItem.id} id={dataItem.id}>
                        {detailDataItem}
                    </div>
                </div>
            );
        }

        return null;
    };

    const isRowChecked = (itemId: string) => checkedRows.findIndex((item: any) => itemId === item.id) !== -1;

    const handleSelect = (dataItem: any) => {
        let x = [...checkedRows];
        if (isRowChecked(dataItem.id)) {
            x = x.filter((checkedItem) => checkedItem.id !== dataItem.id);
        } else {
            x.push(dataItem);
        }
        if (handleSelection && !handleSelection(checkedRows, x)) return;
        setCheckedRows(x);
    };

    const isExpandAllEnabled = (!showPager ? allData : data).find((item) => recordHasDetails(item));

    const debouncedSelection = useCallback(
        debounce((selected, setCurrent, dataItems) => {
            const selectedItemsId = Object.keys(selected);
            if (selectedItemsId.length && setCurrent) {
                const lastSelectedItemId = selectedItemsId[selectedItemsId.length - 1];
                setCurrent(dataItems.find((item: any) => item.id === lastSelectedItemId));
            }
        }, 500),
        [],
    );

    const onSelectionChange = (event: GridSelectionChangeEvent) => {
        const newSelectedState = getSelectedState({
            event,
            selectedState,
            dataItemKey: DATA_ITEM_KEY,
        });
        setSelectedState(newSelectedState);
        debouncedSelection(newSelectedState, setCurrentItem, gridData);
    };

    const getSelectedItems = () => gridData.filter((item) => selectedState[idGetter(item)]);

    const handleChangePageSize = (value: number, triggerCallback: boolean = true) => {
        let numOfPages = Math.floor((allData.length - 1) / value) + 1;
        if (numOfPages === 0) {
            numOfPages = 1;
        }
        setNumPages(numOfPages);

        if (triggerCallback) {
            setPageSize(value);
        }
    };

    useEffect(() => {
        handleChangePageSize(pageSize, false);
    }, [pageSize, allData?.length]);

    // The following useEffect ensures that we give "data-testid"s to the appropriate expanded-to-show
    // "detail-rows".
    useEffect(() => {
        addDetailRowIdToGrid(generalRef);
        calculateHierarchyRowWidth(generalRef);
    });

    const [triggerExcelExport, setTriggerExcelExport] = useState(0);

    const flattenGroupedData = (dataToFlatten: any[]): any[] =>
        dataToFlatten.flatMap((x) => (x.items !== undefined ? flattenGroupedData(x.items) : [x]));

    return (
        <div className={cn('expandable-grid-v2', customKey, className)} key={customKey} data-testid={customKey}>
            {!items?.length && (
                <div className="overlay-without-records" style={{ maxHeight: maxHeight ? +maxHeight + 130 : '100%' }} />
            )}
            {/* NOTE: This component has its own render function, so it should be hidden by default to be able to reuse it here */}
            {exportConfig && (
                <div className="hidden-export">
                    <ExportExcelComponent
                        data={flattenGroupedData(cloneDeep(exportConfig?.useData ? allData : items))}
                        fileName={exportConfig?.fileName ?? 'ExportedData'}
                        columns={(exportConfig?.useColumn ? columnsArray : columns).sort(compareColumnsByOrderIndex)}
                        mapColumns={exportConfig?.mapColumns || null}
                        exportTrigger={triggerExcelExport}
                    />
                </div>
            )}
            <GridHeader
                menu={
                    <GridMenu
                        switchMenu={switchMenu && <SwitchMenu switchMenu={switchMenu} disable={!!disableTools} />}
                        settingsMenu={
                            <SettingsMenu
                                gridKey={gridKey}
                                columns={columnsArray}
                                onColumnsSubmit={onColumnsSubmit}
                                group={group}
                                setGroup={setGroup}
                                showPager={showPager}
                                pageSize={pageSize}
                                setCurrentPage={(page: number) => setCurrentPage(page)}
                                handleChangePageSize={handleChangePageSize}
                                disabled={!!disableTools}
                            />
                        }
                        viewOptionsMenu={
                            gridViewOptions && (
                                <GridViewMenu
                                    wrapperClassName="grid-header-actions"
                                    viewOptions={gridViewOptions}
                                    gridKey={gridKey}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'right',
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                />
                            )
                        }
                        extraMenuItems={extraMenuItems}
                        disableExpandMenu={!!disableTools}
                        checkedRows={selectable?.enabled ? getSelectedItems() : checkedRows}
                        hideDeletedToggle={hideDeletedToggle}
                        showDeletedItems={showDeletedItems}
                        appliedFilters={appliedFilters}
                        clearFilters={clearFilters}
                        showExport={!!exportConfig}
                        exportCallback={() => setTriggerExcelExport(triggerExcelExport + 1)}
                    />
                }
                pager={
                    showPager && (
                        <GridPager
                            page={currentPage}
                            numPages={numPages}
                            onChange={(page: number) => setCurrentPage(page)}
                        />
                    )
                }
                title={title}
                subHeader={subHeader}
                totalRecords={allData?.length}
                showDeletedItems={showDeletedItems}
                showCustomTitle={showCustomTitle}
            />
            <Tooltip anchorElement="target" position="top">
                <Grid
                    onColumnReorder={onColumnReorder}
                    key={gridKey}
                    className={gridClasses}
                    style={{ maxHeight }}
                    ref={(instance: any) => {
                        grid = instance;

                        if (id)
                            // This needs to be here to give external access to the Kendo grid -KM
                            (window as any)[id] = grid;

                        // Set generalRef
                        generalRef.current = grid;
                    }}
                    data={gridData.map((item) => ({
                        ...item,
                        [SELECTED_FIELD]: selectedState[idGetter(item)],
                    }))}
                    pageable={pageable}
                    {...pagingState}
                    reorderable={reorderable}
                    filterOperators={Constants.filterOperators}
                    groupable={groupable}
                    group={group.map((groupItem: IExpandableGridColumnV2) => ({
                        ...groupItem,
                        field: groupItem.groupTitle ?? groupItem.title ?? groupItem.label ?? groupItem.field ?? '',
                    }))}
                    sortable
                    resizable={resizable}
                    sort={sort}
                    scrollable={scrollable}
                    onSortChange={({ sort: newSort }: any) => setSort(newSort)}
                    onDataStateChange={dataStateChange}
                    filter={filter}
                    onFilterChange={({ filter: newFilter }: any) => {
                        setFilter(newFilter);
                        if (setPersistentFilter) setPersistentFilter(newFilter);
                        setCurrentPage(1);
                        setGridKeyAffix((value) => {
                            return ++value;
                        });
                    }}
                    dataItemKey="id"
                    detailExpand={expandedItems}
                    detail={detail || rowActions || showCheckbox ? getItemDetails : null}
                    cells={{
                        headerCell: (props) => {
                            const elCount = [detail, rowActions, !hideCheckbox].filter(Boolean).length;
                            return (
                                <HeaderThElement
                                    columnId={props.thProps?.columnId || ''}
                                    {...props.thProps}
                                    className={props.thProps?.className}
                                    data-width={elCount > 0 ? CUSTOM_COLUMN_WIDTH * elCount : undefined}
                                >
                                    {rowActions && (
                                        <div
                                            style={{ width: '32px' }}
                                            className={cn('actions-wrapper', {
                                                'without-detail': !detail,
                                            })}
                                        >
                                            &nbsp;
                                        </div>
                                    )}
                                    {!props.thProps?.columnId.includes('group') && detail && (
                                        <button
                                            style={{
                                                left: 'auto',
                                                right: !hideCheckbox ? 32 : 0,
                                                maxWidth: '32px',
                                                borderLeft: '1px solid #c3c3c8',
                                            }}
                                            className={cn('expand-btn', {
                                                expanded: areAllItemsExpanded,
                                                'non-expanded': !areAllItemsExpanded,
                                            })}
                                            disabled={!isExpandAllEnabled}
                                            onClick={() => {
                                                data.forEach((dataItem) => {
                                                    expandedItems[dataItem.id] = !areAllItemsExpanded;
                                                });
                                                setAreAllItemsExpanded(!areAllItemsExpanded);
                                            }}
                                            data-testid="expand-button"
                                        >
                                            <FontAwesomeIcon icon={['fas', 'caret-down']} className="icon down-icon" />
                                        </button>
                                    )}
                                    {!props.thProps?.columnId.includes('group') && !hideCheckbox && (
                                        <div
                                            className="expand-btn"
                                            data-testid="expand-button"
                                            style={{
                                                left: 'auto',
                                                right: 0,
                                                maxWidth: '32px',
                                                borderLeft: '1px solid #c3c3c8',
                                            }}
                                        >
                                            <Checkbox
                                                className="cell-checkbox"
                                                size="small"
                                                rounded="none"
                                                checked={areAllRowsSelected}
                                                onChange={() => {
                                                    if (
                                                        handleSelection &&
                                                        !handleSelection(
                                                            checkedRows,
                                                            data.filter((item) => !item.deleted),
                                                        )
                                                    ) {
                                                        return;
                                                    }
                                                    setAreAllRowsSelected(!areAllRowsSelected);
                                                    const updatedData = data.map((dataItem) => {
                                                        dataItem.checked =
                                                            (!rowCheckbox || rowCheckbox(dataItem)) &&
                                                            !areAllRowsSelected;
                                                        return dataItem;
                                                    });
                                                    setCheckedRows(updatedData);
                                                    setData(updatedData);
                                                }}
                                                data-testid="toggle-select-all-rows"
                                            />
                                        </div>
                                    )}
                                </HeaderThElement>
                            );
                        },
                        data: cellRenderV2,
                        hierarchy: {
                            data: (props: GridCustomCellProps) => {
                                const elCount = [detail, rowActions, showCheckbox].filter(Boolean).length;
                                return (
                                    <td
                                        {...props.tdProps}
                                        className={`custom-cell k-grid-content-sticky ${
                                            elCount > 0 ? 'k-hierarchy-cell' : ''
                                        } ${recordHasDetails(props.dataItem) || !hideCheckbox ? 'with-details' : ''} ${
                                            elCount > 1 ? 'multiple-inputs' : ''
                                        }`}
                                        style={{ minHeight: '1px' }}
                                        data-width={
                                            elCount === 1 && rowActions ? CUSTOM_COLUMN_WIDTH * elCount : undefined
                                        }
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                height: '100%',
                                            }}
                                            data-inputcount={elCount}
                                        >
                                            <div
                                                style={{ maxWidth: '32px' }}
                                                className={cn('custom-cell-flex-wrapper', {
                                                    'with-details': recordHasDetails(props.dataItem),
                                                    checkbox: showCheckbox,
                                                })}
                                            >
                                                <div className="actions-checkbox">
                                                    {rowActions && rowActions(props.dataItem) && (
                                                        <div
                                                            className={cn('actions-wrapper', {
                                                                'without-detail': !detail,
                                                            })}
                                                        >
                                                            <ActionsMenuV2
                                                                actions={rowActions(props.dataItem)}
                                                                menuClassName="mt"
                                                                anchorOrigin={{
                                                                    vertical: 'top',
                                                                    horizontal: 'right',
                                                                }}
                                                                transformOrigin={{
                                                                    vertical: 'top',
                                                                    horizontal: 'left',
                                                                }}
                                                                showSingleMenuItem={singleRowAction}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            {recordHasDetails(props.dataItem) && (
                                                <div
                                                    style={{ maxWidth: '32px', borderLeft: '1px solid #c3c3c8' }}
                                                    className={`custom-cell-flex-wrapper with-details 
                                                    ${showCheckbox ? 'checkbox' : ''}`}
                                                >
                                                    <div
                                                        className={`expand-btn ${
                                                            expandedItems[props.dataItem.id]
                                                                ? 'expanded'
                                                                : 'non-expanded'
                                                        }`}
                                                        onClick={() => {
                                                            expandChange(props);
                                                        }}
                                                        data-testid="expand-button"
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={['fas', 'caret-down']}
                                                            className="icon down-icon"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                            {!hideCheckbox && (
                                                <div
                                                    style={{ maxWidth: '32px', borderLeft: '1px solid #c3c3c8' }}
                                                    className={cn('custom-cell-flex-wrapper', {
                                                        'with-details': recordHasDetails(props.dataItem),
                                                        checkbox: showCheckbox,
                                                    })}
                                                >
                                                    <div className="actions-checkbox">
                                                        {showCheckbox &&
                                                            !props.dataItem.deleted &&
                                                            (!rowCheckbox || rowCheckbox(props.dataItem)) && (
                                                                <Checkbox
                                                                    rounded="none"
                                                                    size="small"
                                                                    className="cell-checkbox width-full"
                                                                    checked={isRowChecked(props.dataItem.id)}
                                                                    onChange={() => handleSelect(props.dataItem)}
                                                                    data-testid="row-checkbox"
                                                                />
                                                            )}
                                                    </div>
                                                </div>
                                            )}
                                            {hideCheckbox && !recordHasDetails(props.dataItem) && (
                                                <div className="empty-advanced-detail-row" />
                                            )}
                                        </div>
                                    </td>
                                );
                            },
                        },
                    }}
                    onScroll={onGridScroll}
                    rows={{ data: gridV2RowRender }}
                    selectable={selectable}
                    fixedScroll={fixedScroll}
                    onSelectionChange={onSelectionChange}
                >
                    {!!gridElement &&
                        renderGridV2Columns(
                            filter,
                            gridElement,
                            columnsArray,
                            widthFirstExtraColumn,
                            getGridMinWidth(columnsArray, widthFirstExtraColumn),
                        )}
                </Grid>
            </Tooltip>
        </div>
    );
};

export default ExpandableGridV2;
